import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
    getModelDataByScenario,
    getModelDataByScenarioWithSensitivity,
    compareScenariosFields,
    compareScenarioSensitivityFields,
    compareScenariosFieldsFloatDetailed,
    compareScenarioSensitivityFieldsFloatDetailed,
} from '../../shared/api/compare';
import { FMEPageHeader, useModelContext, ScenarioSensitivitySelector } from '../../components/Layout';
import TailwindSelect from '../../components/Tailwind/TailwindSelect';
import TailwindButton from '../../components/Tailwind/TailwindButton';
import dayjs from 'dayjs';
import snakeToPascal from '../../tools';
import FieldsInitializer from '../../components/ModelDataFields/Input/Initializer';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Cell,
} from 'recharts';

import StringField from '../../components/ModelDataFields/Input/String';
import FloatField from '../../components/ModelDataFields/Input/Float';
import IntField from '../../components/ModelDataFields/Input/Int';
import DateField from '../../components/ModelDataFields/Input/Date';
import EnumField from '../../components/ModelDataFields/Input/Enum';
import BoolField from '../../components/ModelDataFields/Input/Bool';
import TimeSerieField from '../../components/ModelDataFields/Input/TimeSerie';
import TimeSerieTimingField from '../../components/ModelDataFields/Input/TimeSerieTiming';
import RelationField from '../../components/ModelDataFields/Input/Relation';
import ListField from '../../components/ModelDataFields/Input/List';
import CrossField from '../../components/ModelDataFields/Input/CrossField';
import TimingReferenceField from '../../components/ModelDataFields/Input/TimingReference';
import CustomizableTimingReferenceField from '../../components/ModelDataFields/Input/CustomizableTimingReference';

import OutputFloatField from '../../components/ModelDataFields/Output/Float';
import OutputGridField from '../../components/ModelDataFields/Output/Grid';
import DynamicGridField from '../../components/ModelDataFields/Output/DynamicGrid';
import OutputTimeSerieField from '../../components/ModelDataFields/Output/TimeSerie';

const FieldByKindAndType = {
    'Input': {
        'str': StringField,
        'int': IntField,
        'float': FloatField,
        'datetime': DateField,
        'enum': EnumField,
        'bool': BoolField,
        'list': ListField,
        'time_serie_timing': TimeSerieTimingField,
        'time_serie': TimeSerieField,
        'relation': RelationField,
        'timing_reference': TimingReferenceField,
        'customizable_timing_reference': CustomizableTimingReferenceField,
    },
    'Output': {
        'float': OutputFloatField,
        'time_serie': OutputTimeSerieField,
        'grid': OutputGridField,
        'dynamic_grid': DynamicGridField,
    }
}

class FieldErrorBoundary extends React.Component<
    { fallback: React.ReactNode; children: React.ReactNode },
    { hasError: boolean }
> {
    state = { hasError: false };
    static getDerivedStateFromError() { return { hasError: true }; }
    render() {
        return this.state.hasError ? this.fallback : this.props.children;
    }
    get fallback() { return this.props.fallback; }
}

export default function Compare() {
    const { model, currentScenario, currentSensitivity, currentEntity } = useModelContext();

    if (!currentEntity || !currentScenario || !model) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-slate-500 font-medium">Loading...</span>
                </div>
            </div>
        );
    }

    return <CompareContent currentEntity={currentEntity} model={model} currentScenario={currentScenario} currentSensitivity={currentSensitivity} />;
}

// Content component: business logic and rendering
function CompareContent({ currentEntity, model, currentScenario, currentSensitivity }) {
    const [scenarioA, setScenarioA] = useState('');
    const [scenarioB, setScenarioB] = useState('');
    const [outputModelDatas, setOutputModelDatas] = useState<any[]>([]);
    const [rows, setRows] = useState<{ modelDataId: string; fieldId: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [compareResult, setCompareResult] = useState<any>(null);
    const [detailedResult, setDetailedResult] = useState<any>(null);
    const [detailedLoading, setDetailedLoading] = useState(false);
    const [detailedProgress, setDetailedProgress] = useState<{ progress: number; total: number } | null>(null);
    const [compareError, setCompareError] = useState<string | null>(null);
    const [detailedError, setDetailedError] = useState<string | null>(null);
    const [fieldsLoading, setFieldsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'compare' | 'detailed'>('compare');
    const [compareMode, setCompareMode] = useState<'scenarios' | 'sensitivity'>('scenarios');
    const [sensitivityB, setSensitivityB] = useState('');

    type FieldLookupEntry = { field: any; modelDataName: string; fieldName: string };
    type ScenarioLookup = { input: Record<string, FieldLookupEntry>; output: Record<string, FieldLookupEntry>; modelDataNames: Record<string, string> };

    const [lookupA, setLookupA] = useState<ScenarioLookup>({ input: {}, output: {}, modelDataNames: {} });
    const [lookupB, setLookupB] = useState<ScenarioLookup>({ input: {}, output: {}, modelDataNames: {} });

    // Get all scenarios from model
    const scenarios = model.modelScenarios || [];

    const buildFieldLookup = (modelDatas: any[]) => {
        const lookup: Record<string, FieldLookupEntry> = {};
        for (const md of modelDatas) {
            if (!md.fields) continue;
            FieldsInitializer(md.fields);
            for (const [name, field] of Object.entries(md.fields) as [string, any][]) {
                if (field.id) {
                    lookup[field.id.toString()] = { field, modelDataName: md.name, fieldName: name };
                }
            }
        }
        return lookup;
    }

    const buildScenarioLookup = (data: any): ScenarioLookup => {
        const inputs = (data.input || []).filter((md: any) => md.parent_id !== null);
        const outputs = (data.output || []).filter((md: any) => md.parent_id !== null);
        const modelDataNames: Record<string, string> = {};
        for (const md of [...(data.input || []), ...(data.output || [])]) {
            if (md.id) modelDataNames[md.id.toString()] = md.name;
        }
        return {
            input: buildFieldLookup(inputs),
            output: buildFieldLookup(outputs),
            modelDataNames,
        };
    }

    useEffect(() => {
        const bReady = compareMode === 'scenarios' ? !!scenarioB : !!sensitivityB;
        if (scenarioA && bReady) {
            setCompareResult(null);
            setCompareError(null);
            setDetailedResult(null);
            setDetailedError(null);
            setFieldsLoading(true);

            const fetchBoth = compareMode === 'scenarios'
                ? Promise.all([
                    getModelDataByScenario(Number(scenarioA)),
                    getModelDataByScenario(Number(scenarioB)),
                ])
                : Promise.all([
                    getModelDataByScenario(Number(scenarioA)),
                    getModelDataByScenarioWithSensitivity(Number(scenarioA), Number(sensitivityB)),
                ]);

            fetchBoth.then(async ([responseA, responseB]) => {
                if (responseA) {
                    const dataA = await responseA.json();
                    const outputsA = (dataA.output || []).filter((md: any) => md.parent_id !== null);
                    setOutputModelDatas(outputsA);
                    setLookupA(buildScenarioLookup(dataA));
                }
                if (responseB) {
                    const dataB = await responseB.json();
                    setLookupB(buildScenarioLookup(dataB));
                }
            }).finally(() => {
                setFieldsLoading(false);
            });
        } else {
            setOutputModelDatas([]);
            setLookupA({ input: {}, output: {}, modelDataNames: {} });
            setLookupB({ input: {}, output: {}, modelDataNames: {} });
            setCompareResult(null);
            setCompareError(null);
            setDetailedResult(null);
            setDetailedError(null);
        }
    }, [scenarioA, scenarioB, sensitivityB, compareMode]);

    const addRow = () => {
        setRows([...rows, { modelDataId: '', fieldId: '' }]);
        setCompareResult(null);
        setCompareError(null);
        setDetailedResult(null);
        setDetailedError(null);
    }

    const removeRow = (index: number) => {
        setRows(rows.filter((_, i) => i !== index));
        setCompareResult(null);
        setCompareError(null);
        setDetailedResult(null);
        setDetailedError(null);
    }

    const updateRow = (index: number, key: 'modelDataId' | 'fieldId', value: string) => {
        const newRows = [...rows];
        if (key === 'modelDataId') {
            newRows[index] = { modelDataId: value, fieldId: '' };
        } else {
            newRows[index] = { ...newRows[index], [key]: value };
        }
        setRows(newRows);
        setCompareResult(null);
        setCompareError(null);
        setDetailedResult(null);
        setDetailedError(null);
    }

    const completedRows = rows.filter((r) => r.modelDataId && r.fieldId);

    const floatRows = completedRows.filter((r) => {
        const entry = lookupA.output[r.fieldId];
        return entry && entry.field.type === 'float';
    });
    const hasAtLeastOneFloat = floatRows.length > 0;

    const handleCompare = async () => {
        setLoading(true);
        setCompareError(null);
        try {
            const fieldIds = completedRows.map((r) => Number(r.fieldId));
            const response = compareMode === 'sensitivity'
                ? await compareScenarioSensitivityFields(Number(scenarioA), Number(sensitivityB), fieldIds)
                : await compareScenariosFields(Number(scenarioA), Number(scenarioB), fieldIds);
            if (response) {
                if (!response.ok) {
                    const errorData = await response.json().catch(() => null);
                    const detail = errorData?.detail || 'An error occurred';
                    setCompareError(detail);
                    setActiveTab('compare');
                    return;
                }
                const data = await response.json();
                setCompareResult(data);
                setCompareError(null);
                setActiveTab('compare');
            }
        } finally {
            setLoading(false);
        }
    }

    const handleDetailedAnalysis = async () => {
        setDetailedLoading(true);
        setDetailedError(null);
        setDetailedProgress(null);
        const onProgress = (progress: number, total: number) => setDetailedProgress({ progress, total });
        try {
            const fieldIds = floatRows.map((r) => Number(r.fieldId));
            const response = compareMode === 'sensitivity'
                ? await compareScenarioSensitivityFieldsFloatDetailed(Number(scenarioA), Number(sensitivityB), fieldIds, true, onProgress)
                : await compareScenariosFieldsFloatDetailed(Number(scenarioA), Number(scenarioB), fieldIds, onProgress);
            if (response) {
                if (!response.ok) {
                    const errorData = await response.json().catch(() => null);
                    const detail = errorData?.detail || 'An error occurred';
                    setDetailedError(detail);
                    setActiveTab('detailed');
                    return;
                }
                const data = await response.json();
                setDetailedResult(data);
                setDetailedError(null);
                setActiveTab('detailed');
            }
        } finally {
            setDetailedLoading(false);
            setDetailedProgress(null);
        }
    }

    const getFieldsForModelData = (modelDataId: string, floatOnly = false) => {
        const md = outputModelDatas.find((m) => m.id.toString() === modelDataId);
        if (!md || !md.fields) return [];
        return Object.entries(md.fields)
            .filter(([, field]: [string, any]) => !floatOnly || field.type === 'float')
            .map(([name, field]: [string, any]) => ({
                id: field.id.toString(),
                label: field.label || snakeToPascal(name),
            }));
    }

    const getScenarioName = (id: string) => {
        const s = scenarios.find((s) => s.id === id || s.id.toString() === id);
        return s ? s.name : '';
    }

    const getScenario = (id: string) => {
        return scenarios.find((s) => s.id === id || s.id.toString() === id);
    }

    const selectedScenarioA = getScenario(scenarioA);
    const selectedScenarioB = getScenario(scenarioB);
    const isScenarioANotComputed = selectedScenarioA && !selectedScenarioA.is_computed;
    const isScenarioBNotComputed = compareMode === 'scenarios' && selectedScenarioB && !selectedScenarioB.is_computed;
    const hasScenariosNotComputed = isScenarioANotComputed || isScenarioBNotComputed;

    const sensitivityOptions = useMemo(() => {
        return (selectedScenarioA?.sensitivities || []).map((s: any) => ({
            value: s.id.toString(),
            label: s.is_computed ? s.name : `${s.name} (computing...)`,
            disabled: !s.is_computed,
        }));
    }, [selectedScenarioA]);

    const getComparedBName = () => {
        if (compareMode === 'sensitivity') {
            const sens = (selectedScenarioA?.sensitivities || []).find((s: any) => s.id.toString() === sensitivityB);
            return sens?.name ?? sensitivityB;
        }
        return getScenarioName(scenarioB);
    };

    // Create refs for synchronized scrolling of time series
    const scrollRefsMap = React.useRef<Record<number, {
        refA: React.RefObject<HTMLDivElement>;
        refB: React.RefObject<HTMLDivElement>;
        isScrolling: { current: boolean };
    }>>({});

    const renderField = (fieldEntry: FieldLookupEntry | undefined, kind: string, compareWithEntry?: FieldLookupEntry, scrollRef?: React.RefObject<HTMLDivElement>, onScroll?: (e: React.UIEvent<HTMLDivElement>) => void, diffInfo?: any, scenarioLookup?: ScenarioLookup) => {
        if (!fieldEntry) {
            return (
                <p className="text-sm text-slate-500 italic">
                    Field not found
                </p>
            );
        }
        const { field, fieldName } = fieldEntry;
        const componentKind = kind === 'input_field' ? 'Input' : 'Output';
        // For wide/complex field types, always use the Output (read-only) renderer
        const useOutputRenderer = ['time_serie', 'grid', 'dynamic_grid'].includes(field.type);
        const FieldComponent = useOutputRenderer
            ? FieldByKindAndType['Output']?.[field.type]
            : FieldByKindAndType[componentKind]?.[field.type];

        if (field.type === 'list') {
            const rawList = field.currentValue?.value ?? field.value?.value;
            if (!rawList || !Array.isArray(rawList) || rawList.length === 0) {
                return <p className="text-sm text-slate-500 italic">Empty list</p>;
            }
            const elems = field.elems || {};
            const columns = Object.keys(elems).length > 0
                ? Object.keys(elems)
                : Object.keys(rawList[0]).filter((k) => k !== 'id' && k !== 'name');

            // Construire un set de cellules différentes à partir du diffInfo backend
            const diffCells = new Set<string>();
            if (diffInfo?.item_dif) {
                for (const item of diffInfo.item_dif) {
                    if (item && item.item_key !== undefined) {
                        diffCells.add(`${item.item_pos}-${item.item_key}`);
                    }
                }
            }

            // Longueur de la liste comparée pour détecter les lignes supplémentaires
            const compareRawList = compareWithEntry?.field?.currentValue?.value ?? compareWithEntry?.field?.value?.value;
            const compareLen = Array.isArray(compareRawList) ? compareRawList.length : 0;
            const hasLenDif = diffInfo?.len_dif === true;

            const formatCellValue = (cellObj: any, colKey: string) => {
                if (!cellObj || cellObj.value === null || cellObj.value === undefined) return '—';
                const colType = elems[colKey]?.type;
                const val = cellObj.value;
                switch (colType) {
                    case 'datetime':
                    {
                        const d = dayjs.isDayjs(val) ? val : dayjs(val);
                        return d.isValid() ? d.format('DD/MM/YYYY') : String(val);
                    }
                    case 'bool':
                        return val === true || val === 'true' ? 'Yes' : 'No';
                    case 'float':
                        return Number(val).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    case 'int':
                        return Number(val).toLocaleString('fr-FR', { maximumFractionDigits: 0 });
                    case 'enum':
                        return snakeToPascal(String(val));
                    case 'relation':
                    {
                        const mdName = scenarioLookup?.modelDataNames?.[String(val)];
                        return mdName || String(val);
                    }
                    case 'timing_reference':
                    {
                        if (scenarioLookup) {
                            const refEntry = scenarioLookup.input[String(val)] || scenarioLookup.output[String(val)];
                            if (refEntry) {
                                return `${refEntry.modelDataName} — ${snakeToPascal(refEntry.fieldName)}`;
                            }
                        }
                        return String(val);
                    }
                    case 'list_elem_selector':
                    {
                        const targetFieldName = elems[colKey]?.target_list_field;
                        const anchorCol = elems[colKey]?.target_list_column_anchor;
                        if (targetFieldName && anchorCol && scenarioLookup) {
                            const allFields = { ...scenarioLookup.input, ...scenarioLookup.output };
                            const targetEntry = Object.values(allFields).find(
                                (e) => e.modelDataName === fieldEntry.modelDataName && e.fieldName === targetFieldName
                            );
                            if (targetEntry) {
                                const targetList = targetEntry.field.currentValue?.value ?? targetEntry.field.value?.value;
                                if (Array.isArray(targetList)) {
                                    const targetRow = targetList.find((r: any) => String(r.id) === String(val));
                                    if (targetRow?.[anchorCol]) {
                                        return String(targetRow[anchorCol].value ?? targetRow[anchorCol]);
                                    }
                                }
                            }
                        }
                        return String(val);
                    }
                    default:
                        if (colKey.includes('date')) {
                            const d = dayjs.isDayjs(val) ? val : dayjs(val);
                            return d.isValid() ? d.format('DD/MM/YYYY') : String(val);
                        }
                        return String(val);
                }
            };

            return (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr>
                                {columns.map((col) => (
                                    <th key={col} className="text-left px-2 py-1 border-b border-slate-200 font-semibold text-slate-600 text-xs">
                                        {snakeToPascal(col)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rawList.map((row: any, rowIdx: number) => {
                                const isExtraRow = hasLenDif && rowIdx >= compareLen;
                                return (
                                    <tr key={row.id ?? rowIdx} className={rowIdx % 2 === 0 ? 'bg-slate-50' : ''}>
                                        {columns.map((col) => {
                                            const isDiff = diffCells.has(`${rowIdx}-${col}`) || isExtraRow;
                                            return (
                                                <td key={col} className={`px-2 py-1 border-b border-slate-100 ${
                                                    isDiff ? 'bg-amber-100 text-amber-900 font-medium' : 'text-slate-700'
                                                }`}>
                                                    {formatCellValue(row[col], col)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            );
        }

        if (field.type === 'time_serie_timing' || field.type === 'customizable_timing_reference') {
            const raw = field.currentValue?.value ?? field.value?.value;
            const timing = raw?.timing ?? raw;
            if (timing) {
                const startRaw = timing.start_date?.value;
                const endRaw = timing.end_date?.value;
                const startDate = startRaw ? (dayjs.isDayjs(startRaw) ? startRaw : dayjs(startRaw)).format('DD/MM/YYYY') : '—';
                const endDate = endRaw ? (dayjs.isDayjs(endRaw) ? endRaw : dayjs(endRaw)).format('DD/MM/YYYY') : '—';
                const periodicity = timing.periodicity?.value && timing.periodicity.value !== 'null'
                    ? snakeToPascal(timing.periodicity.value)
                    : '—';
                return (
                    <div className="ml-2">
                        <p className="text-sm"><b>Start Date:</b> {startDate}</p>
                        <p className="text-sm"><b>End Date:</b> {endDate}</p>
                        <p className="text-sm"><b>Periodicity:</b> {periodicity}</p>
                    </div>
                );
            }
        }

        const rawFallback = () => {
            const raw = field.currentValue?.value ?? field.value?.value;
            const display = (raw === null || raw === undefined)
                ? '—'
                : (typeof raw === 'object' ? JSON.stringify(raw) : String(raw));
            return <p className="text-sm">{display}</p>;
        };

        if (FieldComponent) {
            return (
                <FieldErrorBoundary fallback={rawFallback()}>
                    <FieldComponent
                        name={snakeToPascal(fieldName)}
                        field={field}
                        reloadState={() => {}}
                        currentState={{}}
                        recreateExcelInput={() => {}}
                        userRights={{ right_to_write: false }}
                        compareWithField={compareWithEntry?.field}
                        scrollRef={scrollRef}
                        onScroll={onScroll}
                    />
                </FieldErrorBoundary>
            );
        }
        return rawFallback();
    }

    // Build scenario options
    const scenarioAOptions = scenarios
        .filter((s) => s.id.toString() !== scenarioB)
        .map((s) => ({ value: s.id.toString(), label: s.name }));

    const scenarioBOptions = scenarios
        .filter((s) => s.id.toString() !== scenarioA)
        .map((s) => ({ value: s.id.toString(), label: s.name }));

    return (
        <div style={{ height: "100%", overflow: "auto" }}>
            <div className="flex flex-col h-full">
                <FMEPageHeader
                    title="Compare Scenarios & Sensitivities"
                    breadcrumb={[model.name]}
                    description="Compare two scenarios to identify differences."
                    actions={<ScenarioSensitivitySelector />}
                />

                <div className="px-8 flex border-b border-slate-200">
                    <button
                        onClick={() => setActiveTab('compare')}
                        className={`px-4 py-2.5 text-sm transition-colors ${
                            activeTab === 'compare'
                                ? 'border-b-2 border-primary text-primary font-bold'
                                : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        Compare
                    </button>
                    <button
                        onClick={() => setActiveTab('detailed')}
                        className={`px-4 py-2.5 text-sm transition-colors ${
                            activeTab === 'detailed'
                                ? 'border-b-2 border-primary text-primary font-bold'
                                : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        Waterfall Analysis
                    </button>
                </div>

                <motion.div
                    className="px-8 py-6"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99], delay: 0.16 } }}
                >
                <div className="flex flex-col gap-4">
                    <p className="text-slate-700">
                        {activeTab === 'compare'
                            ? 'In this section, you can compare scenarios of your financial model.'
                            : 'Decompose the variance between two scenarios by sequentially applying each assumption change and measuring its incremental impact on a key financial indicator.'}
                    </p>

                    {/* Mode toggle */}
                    <div className="flex border-b border-slate-200">
                        <button
                            onClick={() => { setCompareMode('scenarios'); setSensitivityB(''); }}
                            className={`px-4 py-2 text-sm transition-colors ${
                                compareMode === 'scenarios'
                                    ? 'border-b-2 border-primary text-primary font-bold'
                                    : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            <span className="material-symbols-outlined text-sm mr-1 align-middle">compare_arrows</span>
                            Scenario vs Scenario
                        </button>
                        <button
                            onClick={() => { setCompareMode('sensitivity'); setScenarioB(''); }}
                            className={`px-4 py-2 text-sm transition-colors ${
                                compareMode === 'sensitivity'
                                    ? 'border-b-2 border-primary text-primary font-bold'
                                    : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            <span className="material-symbols-outlined text-sm mr-1 align-middle">ssid_chart</span>
                            Scenario vs Sensitivity
                        </button>
                    </div>

                    {/* Scenario Selectors */}
                    <div className="flex flex-row gap-6">
                        <div className="flex-1">
                            <TailwindSelect
                                label={compareMode === 'scenarios' ? 'Scenario A' : 'Scenario'}
                                value={scenarioA}
                                onChange={(value) => { setScenarioA(value); setSensitivityB(''); }}
                                options={scenarioAOptions}
                                placeholder="Select a scenario"
                            />
                        </div>
                        <div className="flex-1">
                            {compareMode === 'scenarios' ? (
                                <TailwindSelect
                                    label="Scenario B"
                                    value={scenarioB}
                                    onChange={(value) => setScenarioB(value)}
                                    options={scenarioBOptions}
                                    placeholder="Select a scenario"
                                />
                            ) : (
                                <TailwindSelect
                                    label="Sensitivity"
                                    value={sensitivityB}
                                    onChange={(value) => setSensitivityB(value)}
                                    options={sensitivityOptions}
                                    placeholder={
                                        !scenarioA
                                            ? 'Select a sensitivity'
                                            : sensitivityOptions.length === 0
                                            ? 'No sensitivities available for this scenario'
                                            : 'Select a sensitivity'
                                    }
                                    disabled={!scenarioA || sensitivityOptions.length === 0}
                                />
                            )}
                        </div>
                    </div>

                    {hasScenariosNotComputed && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
                            <div className="flex items-center mb-2">
                                <span className="material-symbols-outlined text-red-600 text-xl mr-2">warning</span>
                                <p className="text-sm font-bold text-red-600">
                                    Unable to compare scenarios
                                </p>
                            </div>
                            <p className="text-sm ml-8 text-red-600">
                                {isScenarioANotComputed && isScenarioBNotComputed
                                    ? `Both scenarios "${selectedScenarioA?.name}" and "${selectedScenarioB?.name}" have not been computed yet.`
                                    : isScenarioANotComputed
                                    ? `Scenario "${selectedScenarioA?.name}" has not been computed yet.`
                                    : `Scenario "${selectedScenarioB?.name}" has not been computed yet.`}
                            </p>
                            <p className="text-sm ml-8 mt-2 text-red-600">
                                Please complete the scenario inputs and wait for the calculation to finish before comparing scenarios.
                            </p>
                        </div>
                    )}

                    {fieldsLoading && (
                        <div className="mt-6 flex items-center gap-3">
                            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-slate-500">Loading...</span>
                        </div>
                    )}

                    {!fieldsLoading && scenarioA && (compareMode === 'scenarios' ? scenarioB : sensitivityB) && !hasScenariosNotComputed && outputModelDatas.length > 0 && (
                        <div className="mt-6">
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-4">
                                Fields to compare
                            </p>
                            {rows.map((row, index) => {
                                const filteredModelDatas = activeTab === 'detailed'
                                    ? outputModelDatas.filter(md => md.fields && Object.values(md.fields).some((f: any) => f.type === 'float'))
                                    : outputModelDatas;
                                const modelDataOptions = filteredModelDatas.map(md => ({
                                    value: md.id.toString(),
                                    label: md.name
                                })).sort((a, b) => a.label.localeCompare(b.label));
                                const fieldOptions = getFieldsForModelData(row.modelDataId, activeTab === 'detailed').map(f => ({
                                    value: f.id,
                                    label: f.label
                                })).sort((a, b) => a.label.localeCompare(b.label));

                                return (
                                    <div key={index} className="flex flex-row items-center gap-4 mb-3">
                                        <div className="flex-1">
                                            <TailwindSelect
                                                value={row.modelDataId}
                                                onChange={(value) => updateRow(index, 'modelDataId', value)}
                                                options={[
                                                    { value: '', label: 'Select a model data', disabled: true },
                                                    ...modelDataOptions
                                                ]}
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <TailwindSelect
                                                value={row.fieldId}
                                                onChange={(value) => updateRow(index, 'fieldId', value)}
                                                options={[
                                                    { value: '', label: 'Select a field', disabled: true },
                                                    ...fieldOptions
                                                ]}
                                                disabled={!row.modelDataId}
                                            />
                                        </div>
                                        <button
                                            onClick={() => removeRow(index)}
                                            className="p-1 hover:bg-slate-100 rounded transition-colors"
                                            title="Remove field"
                                        >
                                            <span className="material-symbols-outlined text-slate-600 text-xl">delete_forever</span>
                                        </button>
                                    </div>
                                );
                            })}
                            <div className="flex items-center justify-between mt-2">
                                <button
                                    onClick={addRow}
                                    className="flex items-center gap-1 text-sm hover:bg-slate-100 p-1 rounded transition-colors"
                                >
                                    <span className="material-symbols-outlined text-slate-600 text-xl">add</span>
                                    <span className="text-sm">Add a field</span>
                                </button>
                                {completedRows.length > 0 && activeTab === 'compare' && (
                                    <TailwindButton
                                        onClick={handleCompare}
                                        disabled={loading}
                                        variant="primary"
                                    >
                                        {loading && (
                                            <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                        )}
                                        {!loading && (
                                            <span className="material-symbols-outlined text-lg mr-1">compare_arrows</span>
                                        )}
                                        {loading ? 'Comparing...' : 'Compare'}
                                    </TailwindButton>
                                )}
                                {completedRows.length > 0 && activeTab === 'detailed' && (
                                    <div className="flex items-center gap-3">
                                        <TailwindButton
                                            onClick={handleDetailedAnalysis}
                                            disabled={detailedLoading || !hasAtLeastOneFloat}
                                            variant="primary"
                                        >
                                            {detailedLoading && (
                                                <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                            )}
                                            {!detailedLoading && (
                                                <span className="material-symbols-outlined text-lg mr-1">insights</span>
                                            )}
                                            {detailedLoading
                                                ? detailedProgress
                                                    ? `${detailedProgress.progress}/${detailedProgress.total}`
                                                    : 'Initializing...'
                                                : 'Run Waterfall'}
                                        </TailwindButton>
                                        {detailedLoading && detailedProgress && detailedProgress.total > 0 && (
                                            <div className="flex items-center gap-2 min-w-[120px]">
                                                <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-blue-500 rounded-full transition-all duration-300"
                                                        style={{ width: `${Math.round((detailedProgress.progress / detailedProgress.total) * 100)}%` }}
                                                    />
                                                </div>
                                                <span className="text-xs text-slate-500 whitespace-nowrap">
                                                    {Math.round((detailedProgress.progress / detailedProgress.total) * 100)}%
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'compare' && compareError && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
                            <div className="flex items-center mb-2">
                                <span className="material-symbols-outlined text-red-600 text-xl mr-2">warning</span>
                                <p className="text-sm font-bold text-red-600">
                                    Unable to compare
                                </p>
                            </div>
                            <p className="text-sm ml-8 text-red-600">{compareError}</p>
                        </div>
                    )}

                    {activeTab === 'compare' && compareResult && !compareError && !hasScenariosNotComputed && (
                        <div className="mt-6">

                            {/* Missing model datas */}
                            {compareResult.missing_model_datas && (
                                (compareResult.missing_model_datas.in_tree_1?.length > 0 || compareResult.missing_model_datas.in_tree_2?.length > 0) && (
                                <div className="mb-6">
                                    <p className="text-base font-bold mb-4">
                                        Missing Model Datas
                                    </p>

                                    {compareResult.missing_model_datas.in_tree_1?.length > 0 && (
                                        <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded">
                                            <div className="flex items-center mb-2">
                                                <span className="material-symbols-outlined text-orange-600 text-xl mr-2">warning</span>
                                                <p className="text-sm font-bold text-orange-600">
                                                    Only in {getScenarioName(scenarioA)} (not in {getComparedBName()})
                                                </p>
                                            </div>
                                            {compareResult.missing_model_datas.in_tree_1.map((md: any) => (
                                                <p key={md.id} className="text-sm ml-8 text-orange-600">
                                                    • {md.name} ({md.kind})
                                                </p>
                                            ))}
                                        </div>
                                    )}

                                    {compareResult.missing_model_datas.in_tree_2?.length > 0 && (
                                        <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded">
                                            <div className="flex items-center mb-2">
                                                <span className="material-symbols-outlined text-orange-600 text-xl mr-2">warning</span>
                                                <p className="text-sm font-bold text-orange-600">
                                                    Only in {getComparedBName()} (not in {getScenarioName(scenarioA)})
                                                </p>
                                            </div>
                                            {compareResult.missing_model_datas.in_tree_2.map((md: any) => (
                                                <p key={md.id} className="text-sm ml-8 text-orange-600">
                                                    • {md.name} ({md.kind})
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Similar fields (those compared but without differences) */}
                            {(() => {
                                const comparedFieldIds = completedRows.map((r) => Number(r.fieldId));
                                const differenceFieldIds = (compareResult.differences || []).map((diff: any) => diff.id_1 || diff.id_2);
                                const similarFieldIds = comparedFieldIds.filter((id) => !differenceFieldIds.includes(id));

                                if (similarFieldIds.length > 0) {
                                    return (
                                        <div className="mb-6">
                                            <p className="text-base font-bold mb-4">
                                                Similar Fields
                                            </p>
                                            <div className="p-4 bg-green-50 border border-green-200 rounded">
                                                <p className="text-sm mb-2 text-green-600">
                                                    The following fields are identical in both scenarios:
                                                </p>
                                                {similarFieldIds.map((fieldId) => {
                                                    // Try to find the field in output lookups
                                                    const entryA = lookupA.output[fieldId.toString()];
                                                    const entryB = lookupB.output[fieldId.toString()];
                                                    const entry = entryA || entryB;

                                                    if (entry) {
                                                        const fieldDisplayName = `${entry.modelDataName} — ${snakeToPascal(entry.fieldName)}`;
                                                        return (
                                                            <p key={fieldId} className="text-sm ml-8 text-green-700">
                                                                • {fieldDisplayName}
                                                            </p>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })()}

                            {/* Field differences */}
                            {compareResult.differences?.length > 0 && (
                                <div>
                                    <p className="text-base font-bold mb-4">
                                        Field Differences
                                    </p>
                                    {compareResult.differences.map((diff: any, index: number) => {
                                        const lookupKey = diff.kind === 'input_field' ? 'input' : 'output';
                                        const entryA = lookupA[lookupKey][diff.id_1?.toString()];
                                        const entryB = lookupB[lookupKey][diff.id_2?.toString()];
                                        const fieldDisplayName = entryA
                                            ? `${entryA.modelDataName} — ${snakeToPascal(entryA.fieldName)}`
                                            : entryB
                                                ? `${entryB.modelDataName} — ${snakeToPascal(entryB.fieldName)}`
                                                : `Field #${diff.id_1} / #${diff.id_2}`;

                                        const isWideField = entryA?.field?.type === 'time_serie' || entryB?.field?.type === 'time_serie'
                                            || entryA?.field?.type === 'grid' || entryB?.field?.type === 'grid'
                                            || entryA?.field?.type === 'dynamic_grid' || entryB?.field?.type === 'dynamic_grid'
                                            || entryA?.field?.type === 'list' || entryB?.field?.type === 'list';

                                        const isTimeSerie = entryA?.field?.type === 'time_serie' || entryB?.field?.type === 'time_serie';

                                        // Get or create refs for this difference
                                        if (!scrollRefsMap.current[index]) {
                                            scrollRefsMap.current[index] = {
                                                refA: React.createRef<HTMLDivElement>(),
                                                refB: React.createRef<HTMLDivElement>(),
                                                isScrolling: { current: false }
                                            };
                                        }

                                        const { refA: scrollRefA, refB: scrollRefB, isScrolling: isScrollingRef } = scrollRefsMap.current[index];

                                        const handleScrollA = (e: React.UIEvent<HTMLDivElement>) => {
                                            if (!isScrollingRef.current && scrollRefB.current) {
                                                isScrollingRef.current = true;
                                                scrollRefB.current.scrollLeft = (e.target as HTMLDivElement).scrollLeft;
                                                requestAnimationFrame(() => {
                                                    isScrollingRef.current = false;
                                                });
                                            }
                                        };

                                        const handleScrollB = (e: React.UIEvent<HTMLDivElement>) => {
                                            if (!isScrollingRef.current && scrollRefA.current) {
                                                isScrollingRef.current = true;
                                                scrollRefA.current.scrollLeft = (e.target as HTMLDivElement).scrollLeft;
                                                requestAnimationFrame(() => {
                                                    isScrollingRef.current = false;
                                                });
                                            }
                                        };

                                        return (
                                            <div key={index} className="mb-6 p-4 border border-slate-200 rounded">
                                                <div className="flex items-center gap-2 mb-4">
                                                    <p className="text-sm font-bold">
                                                        {fieldDisplayName}
                                                    </p>
                                                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                                                        diff.kind === 'input_field'
                                                            ? 'bg-blue-100 text-blue-700'
                                                            : 'bg-purple-100 text-purple-700'
                                                    }`}>
                                                        {diff.kind === 'input_field' ? 'Input' : 'Output'}
                                                    </span>
                                                </div>
                                                <div className={`flex ${isWideField ? 'flex-col gap-4' : 'flex-row gap-8'}`}>
                                                    <div className={`${isWideField ? '' : 'flex-1'} overflow-hidden`}>
                                                        <p className="text-xs font-bold text-slate-600 mb-2 block">
                                                            {getScenarioName(scenarioA)}
                                                        </p>
                                                        {renderField(entryA, diff.kind, entryB, isTimeSerie ? scrollRefA : undefined, isTimeSerie ? handleScrollA : undefined, diff, lookupA)}
                                                    </div>
                                                    <div className={`${isWideField ? '' : 'flex-1'} overflow-hidden`}>
                                                        <p className="text-xs font-bold text-slate-600 mb-2 block">
                                                            {getComparedBName()}
                                                        </p>
                                                        {renderField(entryB, diff.kind, entryA, isTimeSerie ? scrollRefB : undefined, isTimeSerie ? handleScrollB : undefined, diff, lookupB)}
                                                    </div>
                                                </div>
                                                {diff.dif && (
                                                    <div className="mt-4 p-3 bg-slate-50 rounded">
                                                        <p className="text-sm text-slate-700">
                                                            {diff.dif}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {compareResult.differences?.length === 0 &&
                                compareResult.missing_model_datas?.in_tree_1?.length === 0 &&
                                compareResult.missing_model_datas?.in_tree_2?.length === 0 && (
                                <p className="text-base text-center text-slate-600">
                                    No differences found between the two scenarios.
                                </p>
                            )}
                        </div>
                    )}

                    {activeTab === 'detailed' && detailedError && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
                            <div className="flex items-center mb-2">
                                <span className="material-symbols-outlined text-red-600 text-xl mr-2">warning</span>
                                <p className="text-sm font-bold text-red-600">
                                    Unable to run waterfall analysis
                                </p>
                            </div>
                            <p className="text-sm ml-8 text-red-600">{detailedError}</p>
                        </div>
                    )}

                    {/* Waterfall Analysis Results */}
                    {activeTab === 'detailed' && detailedResult && !detailedError && !hasScenariosNotComputed && (
                        <DetailedAnalysisSection
                            detailedResult={detailedResult}
                            scenarioAName={getScenarioName(scenarioA)}
                            scenarioBName={getComparedBName()}
                            lookupA={lookupA}
                            completedRows={floatRows}
                        />
                    )}
                </div>
            </motion.div>
            </div>
        </div>
    );
}


// --- Waterfall Analysis Section ---

const WATERFALL_COLORS = {
    endpoint: '#135bec',
    positive: '#10b981',
    negative: '#ef4444',
};

const FIELD_COLORS = ['#135bec', '#8b5cf6', '#f59e0b', '#ec4899', '#06b6d4', '#f97316', '#10b981', '#ef4444'];

/** Regrouper les différences par order (les timing_reference partagent le même order même si non consécutives). */
function groupDifferencesByOrder(differences: any[]): { order: number; diffs: any[]; field_impacts: any }[] {
    if (!differences || !Array.isArray(differences)) return [];
    const map = new Map<number, { order: number; diffs: any[]; field_impacts: any }>();
    for (const diff of differences) {
        const existing = map.get(diff.order);
        if (existing) {
            existing.diffs.push(diff);
        } else {
            map.set(diff.order, { order: diff.order, diffs: [diff], field_impacts: diff.field_impacts });
        }
    }
    return Array.from(map.values()).sort((a, b) => a.order - b.order);
}

function buildGroupLabel(diffs: any[]): string {
    if (diffs.length === 1) {
        return `${snakeToPascal(diffs[0].field_name)}\n(${diffs[0].model_data_name})`;
    }
    return `Timing\n(${diffs.length} fields)`;
}

function buildWaterfallData(detailedResult: any, fieldIds: number[], scenarioAName: string, scenarioBName: string) {
    const { baseline_values, differences } = detailedResult;
    const data: any[] = [];
    const groups = groupDifferencesByOrder(differences);

    // Première barre : baseline (valeurs absolues scénario A)
    const baselineEntry: any = { name: scenarioAName, type: 'endpoint' };
    for (const fid of fieldIds) {
        baselineEntry[`base_${fid}`] = 0;
        baselineEntry[`value_${fid}`] = baseline_values[fid] ?? 0;
        baselineEntry[`cumulative_${fid}`] = baseline_values[fid] ?? 0;
        baselineEntry[`sign_${fid}`] = 'endpoint';
    }
    data.push(baselineEntry);

    // Barres intermédiaires : une barre par groupe (order)
    let prevValues: Record<number, number> = {};
    for (const fid of fieldIds) {
        prevValues[fid] = baseline_values[fid] ?? 0;
    }

    for (const group of groups) {
        // Skip les groupes sans field_impacts (compute_not_possible)
        if (!group.field_impacts) continue;

        const entry: any = {
            name: buildGroupLabel(group.diffs),
            type: 'delta',
        };
        for (const fid of fieldIds) {
            const currentVal = group.field_impacts[fid] ?? 0;
            const delta = currentVal - prevValues[fid];
            if (delta >= 0) {
                entry[`base_${fid}`] = prevValues[fid];
                entry[`value_${fid}`] = delta;
                entry[`sign_${fid}`] = '+';
            } else {
                entry[`base_${fid}`] = currentVal;
                entry[`value_${fid}`] = Math.abs(delta);
                entry[`sign_${fid}`] = '-';
            }
            entry[`cumulative_${fid}`] = currentVal;
            entry[`delta_${fid}`] = delta;
        }
        data.push(entry);
        for (const fid of fieldIds) {
            prevValues[fid] = group.field_impacts[fid] ?? 0;
        }
    }

    // Dernière barre : valeur finale (scénario B = dernier field_impacts)
    const finalEntry: any = { name: scenarioBName, type: 'endpoint' };
    for (const fid of fieldIds) {
        finalEntry[`base_${fid}`] = 0;
        finalEntry[`value_${fid}`] = prevValues[fid];
        finalEntry[`cumulative_${fid}`] = prevValues[fid];
        finalEntry[`sign_${fid}`] = 'endpoint';
    }
    data.push(finalEntry);

    return data;
}

function WaterfallTooltip({ active, payload, label, fieldPercentageMap }: any) {
    if (!active || !payload || payload.length === 0) return null;

    // Filtrer les barres visibles (pas les bases transparentes)
    const visibleBars = payload.filter((p: any) => p.dataKey.startsWith('value_'));
    if (visibleBars.length === 0) return null;

    const dataPoint = visibleBars[0]?.payload;

    return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
            <p className="text-sm font-semibold text-slate-800 mb-2 whitespace-pre-line">{label}</p>
            {visibleBars.map((bar: any, idx: number) => {
                const fid = bar.dataKey.replace('value_', '');
                const cumulative = dataPoint[`cumulative_${fid}`];
                const delta = dataPoint[`delta_${fid}`];
                const sign = dataPoint[`sign_${fid}`];
                const fieldName = bar.name;
                const isPct = fieldPercentageMap?.[fid];
                const precision = 2;
                const suffix = isPct ? ' %' : '';

                return (
                    <div key={idx} className="text-sm">
                        <span className="font-medium" style={{ color: bar.fill }}>{fieldName}</span>
                        <span className="text-slate-600 ml-2">
                            {cumulative != null ? Number(isPct ? cumulative * 100 : cumulative).toLocaleString('fr-FR', { maximumFractionDigits: precision }) + suffix : '—'}
                        </span>
                        {sign && sign !== 'endpoint' && delta != null && (
                            <span className={`ml-2 ${delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {delta >= 0 ? '+' : ''}{Number(isPct ? delta * 100 : delta).toLocaleString('fr-FR', { maximumFractionDigits: precision })}{suffix}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function formatYAxis(value: number, isPercentage?: boolean) {
    if (isPercentage) {
        return `${(value * 100).toFixed(2)} %`;
    }
    if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
    if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(2)}k`;
    return value.toFixed(2);
}

function DetailedAnalysisSection({
    detailedResult,
    scenarioAName,
    scenarioBName,
    lookupA,
    completedRows,
}: {
    detailedResult: any;
    scenarioAName: string;
    scenarioBName: string;
    lookupA: any;
    completedRows: { modelDataId: string; fieldId: string }[];
}) {
    const fieldIds = completedRows.map((r) => Number(r.fieldId));

    const fieldNames: Record<number, string> = {};
    const fieldPercentageMap: Record<number, boolean> = {};
    for (const fid of fieldIds) {
        const entry = lookupA.output[fid.toString()];
        fieldNames[fid] = entry
            ? `${entry.modelDataName} — ${snakeToPascal(entry.fieldName)}`
            : `Field #${fid}`;
        fieldPercentageMap[fid] = !!entry?.field?.is_percentage;
    }

    const waterfallDataByField = useMemo(
        () => {
            const result: Record<number, any[]> = {};
            for (const fid of fieldIds) {
                result[fid] = buildWaterfallData(detailedResult, [fid], scenarioAName, scenarioBName);
            }
            return result;
        },
        [detailedResult, fieldIds.join(','), scenarioAName, scenarioBName]
    );

    const { differences, missing_model_datas, baseline_values } = detailedResult;

    return (
        <div className="mt-6">
            <hr className="border-t border-slate-200 mb-6" />
            <p className="text-base font-bold mb-6">Waterfall Analysis</p>

            {/* Waterfall Charts — un par field */}
            {fieldIds.map((fid, idx) => {
                const data = waterfallDataByField[fid];
                return (
                    <div key={fid} className="mb-8 p-4 border border-slate-200 rounded bg-white">
                        <p className="text-sm font-bold mb-4">
                            {fieldNames[fid]}
                        </p>
                        <ResponsiveContainer width="100%" height={Math.max(400, data.length * 30)}>
                            <BarChart data={data} margin={{ top: 10, right: 30, left: 20, bottom: 60 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    tick={{ fill: '#64748b', fontSize: 11 }}
                                    stroke="#cbd5e1"
                                    interval={0}
                                    angle={-30}
                                    textAnchor="end"
                                    height={80}
                                />
                                <YAxis
                                    tick={{ fill: '#64748b', fontSize: 12 }}
                                    stroke="#cbd5e1"
                                    tickFormatter={(v: number) => formatYAxis(v, fieldPercentageMap[fid])}
                                />
                                <Tooltip content={<WaterfallTooltip fieldPercentageMap={fieldPercentageMap} />} />
                                <Bar
                                    dataKey={`base_${fid}`}
                                    stackId="stack"
                                    fill="transparent"
                                    name={`base_${fid}`}
                                    legendType="none"
                                />
                                <Bar
                                    dataKey={`value_${fid}`}
                                    stackId="stack"
                                    name={fieldNames[fid]}
                                >
                                    {data.map((entry, i) => {
                                        const sign = entry[`sign_${fid}`];
                                        let fill: string;
                                        if (sign === 'endpoint') {
                                            fill = FIELD_COLORS[idx % FIELD_COLORS.length];
                                        } else if (sign === '+') {
                                            fill = WATERFALL_COLORS.positive;
                                        } else {
                                            fill = WATERFALL_COLORS.negative;
                                        }
                                        return <Cell key={i} fill={fill} />;
                                    })}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                );
            })}

            {/* Missing model datas */}
            {missing_model_datas && (
                (missing_model_datas.in_tree_1?.length > 0 || missing_model_datas.in_tree_2?.length > 0) && (
                <div className="mb-6">
                    <p className="text-sm font-bold mb-4">Missing Model Datas</p>
                    {missing_model_datas.in_tree_1?.length > 0 && (
                        <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded">
                            <div className="flex items-center mb-2">
                                <span className="material-symbols-outlined text-orange-600 text-xl mr-2">warning</span>
                                <p className="text-sm font-bold text-orange-600">
                                    Only in {scenarioAName} (not in {scenarioBName})
                                </p>
                            </div>
                            {missing_model_datas.in_tree_1.map((md: any) => (
                                <p key={md.id} className="text-sm ml-8 text-orange-600">
                                    {md.name} ({md.kind})
                                </p>
                            ))}
                        </div>
                    )}
                    {missing_model_datas.in_tree_2?.length > 0 && (
                        <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded">
                            <div className="flex items-center mb-2">
                                <span className="material-symbols-outlined text-orange-600 text-xl mr-2">warning</span>
                                <p className="text-sm font-bold text-orange-600">
                                    Only in {scenarioBName} (not in {scenarioAName})
                                </p>
                            </div>
                            {missing_model_datas.in_tree_2.map((md: any) => (
                                <p key={md.id} className="text-sm ml-8 text-orange-600">
                                    {md.name} ({md.kind})
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            ))}

            {/* Input differences with order and impacts (groupées par order) */}
            {differences?.length > 0 && (
                <div>
                    <p className="text-sm font-bold mb-4">Input Differences Applied</p>
                    {groupDifferencesByOrder(differences).map((group) => {
                        const prevGroup = groupDifferencesByOrder(differences).find((g) => g.order === group.order - 1);
                        return (
                            <div key={group.order} className="mb-4 p-4 border border-slate-200 rounded">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold">
                                        {group.order}
                                    </span>
                                    {group.diffs.length === 1 ? (
                                        <>
                                            <p className="text-sm font-bold">
                                                {group.diffs[0].model_data_name} — {snakeToPascal(group.diffs[0].field_name)}
                                            </p>
                                            <span className="text-xs font-bold px-2 py-1 rounded bg-blue-100 text-blue-700">
                                                {group.diffs[0].field_type}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-sm font-bold">Timing</p>
                                            <span className="text-xs font-bold px-2 py-1 rounded bg-blue-100 text-blue-700">
                                                {group.diffs.length} fields
                                            </span>
                                        </>
                                    )}
                                </div>
                                {/* Détail de chaque diff du groupe */}
                                {group.diffs.map((diff: any, i: number) => (
                                    <div key={i} className="ml-8 mb-2">
                                        {group.diffs.length > 1 && (
                                            <p className="text-sm font-medium text-slate-700">
                                                {diff.model_data_name} — {snakeToPascal(diff.field_name)}
                                            </p>
                                        )}
                                        {diff.dif && (
                                            <p className="text-sm text-slate-600">
                                                {diff.dif}
                                            </p>
                                        )}
                                    </div>
                                ))}
                                {/* Compute not possible */}
                                {group.diffs.some((d: any) => d.compute_not_possible) ? (
                                    <div className="ml-8 mt-3 flex items-center gap-2 px-3 py-2 rounded bg-orange-50 border border-orange-200">
                                        <span className="material-symbols-outlined text-orange-600 text-lg">block</span>
                                        <p className="text-sm text-orange-700">
                                            Impact cannot be computed — the referenced element in scenario B does not exist in scenario A.
                                        </p>
                                    </div>
                                ) : (
                                    /* Impacts (identiques pour tout le groupe) */
                                    group.field_impacts && (
                                        <div className="ml-8 mt-3 flex flex-wrap gap-4">
                                            {fieldIds.map((fid) => {
                                                const impactVal = group.field_impacts[fid];
                                                const prevVal = prevGroup?.field_impacts
                                                    ? prevGroup.field_impacts[fid]
                                                    : baseline_values[fid];
                                                const delta = impactVal != null && prevVal != null ? impactVal - prevVal : null;
                                                const isPct = fieldPercentageMap[fid];
                                                const precision = 2;
                                                const suffix = isPct ? ' %' : '';
                                                return (
                                                    <div key={fid} className="text-sm">
                                                        <span className="text-slate-500">{fieldNames[fid]}:</span>{' '}
                                                        <span className="font-medium">
                                                            {impactVal != null ? Number(isPct ? impactVal * 100 : impactVal).toLocaleString('fr-FR', { maximumFractionDigits: precision }) + suffix : '—'}
                                                        </span>
                                                        {delta != null && (
                                                            <span className={`ml-1 ${delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                                {delta >= 0 ? '+' : ''}{Number(isPct ? delta * 100 : delta).toLocaleString('fr-FR', { maximumFractionDigits: precision })}{suffix}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {differences?.length === 0 && (
                <p className="text-base text-center text-slate-600">
                    No input differences found between the two scenarios.
                </p>
            )}
        </div>
    );
}
